// Which tiers a plan's Part D deductible applies to (drschoice/flow#731).
//
// The truth is the plan's CMS values, from the PBP Benefits file
// (oracle-spa#91), sent on each MAPD/PDP quote (oracle#127):
//   drug_formulary_tiers_num          tier count; null on Defined Standard plans
//   drug_deductible_applicable_tiers  exact tiers, 1-based; [] = no deductible
//   drug_deductible_all_tiers         true = every drug, Defined Standard too
// An agent's plan_overrides list wins when it names tiers. It is saved per
// plan for every client. Lists from the old "Tier N+" select run up to tier 5,
// and "Tier 0+" meant all tiers. With neither, the caller guesses from the
// client's own drugs.

import _ from 'lodash'

const range = (from, to) => _.range(from, to + 1)
const tierList = tiers => _.sortBy(_.uniq((tiers || []).filter(t => t > 0)))
const coversEveryTier = (tiers, tiersNum) => !!tiersNum && _.isEqual(tiers, range(1, tiersNum))

// The quote's CMS values, or null when it carries none of them.
export function cmsDeductibleTiers (plan) {
    const tiers = plan?.drug_deductible_applicable_tiers
    const all = plan?.drug_deductible_all_tiers
    const tiersNum = plan?.drug_formulary_tiers_num
    if (_.isNil(tiers) && _.isNil(all) && _.isNil(tiersNum)) { return null }
    return { tiers: tiers ?? null, all: all ?? null, tiersNum: tiersNum ?? null }
}

// { tiers, all, source } for an override list and the plan's CMS values, or
// null when neither says anything. An empty override gives way to CMS values:
// most are phantom writes, and a plan without a deductible already says so
// with its $0. Without CMS values it still means no tiers, never a guess.
export function resolveDeductibleTiers (override, cms) {
    if (override && !override.length && !cms) {
        return { tiers: [], all: false, source: 'override' }
    }
    if (override?.length) {
        if (override.includes(0)) {
            return { tiers: cms?.tiersNum ? range(1, cms.tiersNum) : [], all: true, source: 'override' }
        }
        const tiers = tierList(override)
        return { tiers, all: coversEveryTier(tiers, cms?.tiersNum), source: 'override' }
    }
    if (cms) {
        const tiers = tierList(cms.tiers)
        return { tiers, all: cms.all === true || coversEveryTier(tiers, cms.tiersNum), source: 'cms' }
    }
    return null
}

// "2–5", "3 and 5", "1–3 and 5": runs of three or more as a range.
function joinTiers (tiers) {
    const runs = []
    tiers.forEach((t) => {
        const run = _.last(runs)
        if (run && _.last(run) === t - 1) { run.push(t) } else { runs.push([t]) }
    })
    const parts = _.flatMap(runs, run => (run.length >= 3 ? [`${run[0]}–${_.last(run)}`] : run.map(String)))
    return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${_.last(parts)}`
}

// What the deductible applies to, "all prescriptions" or "tier 2–5
// prescriptions", or null when the tiers are unknown. A list holding 0 is an
// old "Tier 0+" pick that never went through resolveDeductibleTiers.
export function deductibleTiersText (tiers, all) {
    if (all || tiers?.includes(0)) { return 'all prescriptions' }
    const list = tierList(tiers)
    return list.length ? `tier ${joinTiers(list)} prescriptions` : null
}

// The same, for the plan card's picker button: "Tier 2–5", "All tiers".
export function deductibleTiersLabel (tiers, all) {
    if (all || tiers?.includes(0)) { return 'All tiers' }
    const list = tierList(tiers)
    return list.length ? `Tier ${joinTiers(list)}` : 'Tiers unknown'
}

// The tiers an agent can pick: 1 to the plan's tier count, none on a Defined
// Standard plan (all tiers, no count), and 1-6 when nothing is known.
export function pickerTiers (cms) {
    if (cms?.tiersNum) { return range(1, cms.tiersNum) }
    if (cms?.all) { return [] }
    return range(1, 6)
}

// The exact tiers to save for a pick, or null when the pick changes nothing.
export function tiersToSave (shown, picked) {
    const tiers = tierList(picked)
    return !tiers.length || _.isEqual(tiers, tierList(shown)) ? null : tiers
}
